<template>
  <div class="header-intro-block bg-gray-100">
    <div class="container mx-auto pt-4 md:pt-8">
      <div class="mx-auto flex flex-col md:flex-row md:items-start md:gap-8">
        <div class="flex w-full flex-col md:w-[50%]">
          <!-- Mascots Block -->
          <div v-if="mascot && mascot.length > 0" class="order-1 mb-3 md:order-3 md:mb-0 md:mt-6">
            <div class="flex justify-start md:justify-end">
              <mascot-display
                :mascot-ids="mascot"
                layout="horizontal"
                direction="left"
                size="medium"
                :show-height-difference="true"
                class="justify-center"
              />
            </div>
          </div>
          <!-- Text Content -->
          <div
            class="order-2 mb-4 flex flex-col items-start justify-center gap-5 bg-white p-6 shadow-sm md:order-1 md:mb-0 md:p-8"
            :class="{
              // When mascots are present
              'rounded-tl-0  rounded-bl-[20px] rounded-br-[20px] rounded-tr-[20px] md:rounded-bl-[20px] md:rounded-br-[0px] md:rounded-tl-[20px] md:rounded-tr-[20px]':
                hasMascots,
              // When no mascots - full rounded corners
              'rounded-[20px]': !hasMascots,
            }"
          >
            <!-- Optional Kicker -->
            <div v-if="kicker" class="text-left font-headline text-h5 font-normal text-purple-dark">
              {{ kicker }}
            </div>

            <!-- Title -->
            <h1 class="text-left font-headline text-h2 font-normal text-black">
              {{ title }}
            </h1>

            <!-- Preamble -->
            <p v-if="preamble" class="font-headline-normal text-left text-body text-black">
              {{ preamble }}
            </p>

            <!-- Optional Button -->
            <app-button
              v-if="buttonText && buttonUrl"
              :as="'nuxt-link'"
              :to="buttonUrl"
              variant="ljusrosa"
              size="desktop"
              position="right"
              arrow-type="double"
              class="inline-block"
            >
              {{ buttonText }}
            </app-button>
          </div>

          <!-- Body content when containImage is true -->
          <div v-if="containImage && body" class="order-4 mb-8 mt-4 md:order-4 md:mb-0">
            <div
              class="rounded-tl-0 rounded-bl-[20px] rounded-br-[20px] rounded-tr-[20px] bg-white p-6 shadow-sm md:rounded-bl-[20px] md:rounded-br-[20px] md:rounded-tl-[20px] md:rounded-tr-[0px] md:p-8"
            >
              <contentful-rich-text :document="body" />
            </div>
          </div>
        </div>
        <!-- Image (Bottom on Mobile, Right on Desktop) -->
        <div class="order-3 w-full md:order-2 md:w-[50%]">
          <div v-if="imageUrl" class="overflow-hidden rounded-[20px] bg-white shadow-sm">
            <app-image
              :url="imageUrl"
              responsive="width"
              sizes="xs:100vw sm:100vw md:100vw lg:70vw xl:50vw 2xl:50vw"
              :class="
                containImage
                  ? 'h-full w-full object-contain object-center'
                  : 'h-full w-full object-cover md:max-h-[420px] md:min-h-[420px]'
              "
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import { fixImageUrlProtocol } from '@/utils/contentfulHelpers'

interface Props {
  title: string
  preamble?: string
  body?: any // Contentful rich text body content
  image?: any
  mascot?: string[]
  kicker?: string
  buttonText?: string
  buttonUrl?: string
  includes?: any // Contentful includes for resolving assets
  containImage?: boolean // Use object-contain instead of object-cover for tall images
}

const props = defineProps<Props>()

// Determine if mascots are present
const hasMascots = computed(() => {
  return props.mascot && props.mascot.length > 0
})

// Get image URL from Contentful asset using the proper helper function
const imageUrl = computed(() => {
  if (props.image) {
    let image = ''
    if (props.image instanceof Array) {
      image = props.image[0]?.url
    } else if (typeof props.image === 'string') {
      image = props.image
    }
    return fixImageUrlProtocol(image)
  }
  // If all else fails, return default image
  return 'https://res.cloudinary.com/djurensratt/image/upload/rdk/glada-kycklingar-utomhus.jpg'
})
</script>
